// Single source of truth for what is actually Live, in Beta, or Planned — the
// "honesty as a feature" recommendation from the strategy review. Statuses are
// derived from what genuinely ships in this codebase, not from marketing. When
// a capability moves, change it here and it updates everywhere (roadmap page,
// and any feature list that reads from this).
//
// Rule of thumb used to set these:
//   live    — implemented and working end-to-end in the product today.
//   beta    — implemented and shipping, but new / depends on a pending DB sync
//             or an enabled model; expect rough edges.
//   planned — designed in the strategy but not built yet. Never sold as present.

export type FeatureStatus = "live" | "beta" | "planned";
export type Feature = { name: string; status: FeatureStatus; note?: string };
export type FeatureGroup = { group: string; blurb: string; features: Feature[] };

export const FEATURE_GROUPS: FeatureGroup[] = [
  {
    group: "Trust & identity",
    blurb: "The layer that makes a twin worth believing — the moat.",
    features: [
      { name: "Verified company seal (domain ownership)", status: "beta" },
      { name: "Company identity on the twin & Agent Card", status: "beta" },
      { name: "Verified proofs & trust score", status: "live" },
      { name: "Swiss company, EU-hosted data, GDPR/FADP, DPA & sub-processors", status: "live" },
      {
        name: "Commercial-register (Zefix) auto-verification",
        status: "planned",
        note: "Register number is recorded today; automatic registry check is next.",
      },
      {
        name: "Statement register — timestamped, hash-anchored record of every answer",
        status: "beta",
      },
      { name: "Commitment levels (informational · indicative · binding)", status: "beta" },
    ],
  },
  {
    group: "The Twin",
    blurb: "Your website becomes a grounded, answering entity.",
    features: [
      { name: "AI Twin from your website, grounded with sources", status: "live" },
      { name: "Knowledge base — crawl + document upload", status: "live" },
      { name: "Personality, voice & behaviour tuning", status: "live" },
      { name: "Multilingual (7 languages)", status: "live" },
      { name: "Live demo twin — try any company without an account", status: "beta" },
      { name: "Honest 'I don't know' + human handoff", status: "live" },
      { name: "Knowledge freshness scoring + contradiction check", status: "beta" },
    ],
  },
  {
    group: "Action — where answers become revenue",
    blurb: "The twin doesn't just answer; it acts.",
    features: [
      { name: "Lead capture, scoring & full contact form", status: "live" },
      {
        name: "Human handoff with context — who, what, budget, timing, open points",
        status: "beta",
      },
      { name: "Real CRM with customer records (Kundenakten)", status: "beta" },
      { name: "In-chat appointment booking + calendar links", status: "live" },
      { name: "Paid service requests & quotes with commission", status: "live" },
      { name: "Twin-to-Twin deals (proposal → signature)", status: "live" },
      { name: "Lead webhooks (HMAC-signed)", status: "live" },
      { name: "Native CRM integrations (HubSpot, Pipedrive, Bexio…)", status: "planned" },
    ],
  },
  {
    group: "The Network",
    blurb: "Companies find, qualify and transact with each other.",
    features: [
      { name: "Public twin profile & marketplace", status: "live" },
      { name: "Business Exchange — listings & nightly matching", status: "live" },
      { name: "Proactive Deal Radar — daily network scan + outreach drafts", status: "beta" },
      { name: "Twin-to-Twin pre-qualification handshake", status: "beta" },
      { name: "Response & reputation scoring", status: "beta" },
      { name: "Shared deal room (documents, NDA, milestones)", status: "planned" },
    ],
  },
  {
    group: "Distribution",
    blurb: "Be found inside the tools where decisions are made.",
    features: [
      { name: "MCP server — reachable in ChatGPT & Claude", status: "live" },
      { name: "A2A Agent Card at /.well-known/agent.json", status: "beta" },
      { name: "robots.txt & multilingual sitemap (hreflang)", status: "live" },
      { name: "Embed widget (one script tag)", status: "live" },
      { name: "Machine-readable full text (llms.txt + llms-full.txt)", status: "live" },
      { name: "WhatsApp / Slack / Teams channels", status: "planned" },
      { name: "Agency multi-twin / reseller workspace", status: "planned" },
    ],
  },
  {
    group: "Measurement",
    blurb: "The bait and the proof.",
    features: [
      { name: "Twin-Check — free public AI-readability audit", status: "beta" },
      { name: "Dashboard analytics (conversations, leads, revenue)", status: "live" },
      { name: "Knowledge-gap report", status: "live" },
      { name: "Agent-traffic analytics (who queried you, for what)", status: "beta" },
      { name: "AI-visibility trend across models", status: "planned" },
    ],
  },
];
